import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  MdArrowBack,
  MdArrowForward,
  MdAdd,
  MdEdit,
  MdDelete,
  MdSave,
  MdQuestionAnswer,
} from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import { useCourse } from "../../context/CourseContext";
import Modal from "components/ui/Modal";
import Select from "components/ui/Select";
import ConfirmDialog from "components/ui/ConfirmDialog";
import { Skeleton } from "components/ui/Skeleton";
import EmptyState from "components/ui/EmptyState";
import QuizOptionsEditor from "./components/QuizOptionsEditor";
import {
  instructorQuizzesService,
  type CourseQuiz,
  type QuizUpsertBody,
} from "services/instructorQuizzesService";
import {
  instructorQuizQuestionsService,
  type QuizQuestion,
  type QuizQuestionOption,
  type QuizQuestionType,
  type QuizQuestionUpsertBody,
} from "services/instructorQuizQuestionsService";
import { toastError } from "lib/toast";

function validateQuestion(body: QuizQuestionUpsertBody, isArabic: boolean): string | null {
  if (!body.question.trim()) return isArabic ? "نص السؤال مطلوب" : "Question text is required";
  if (!Number.isFinite(body.score) || body.score <= 0)
    return isArabic ? "الدرجة يجب أن تكون رقمًا موجبًا" : "Score must be a positive number";
  if (body.options.some((o) => !o.answer.trim()))
    return isArabic ? "كل الخيارات يجب أن تحتوي على نص" : "All options must have text";
  const correct = body.options.filter((o) => o.is_correct).length;
  if (body.type === "true_false") {
    if (body.options.length !== 2)
      return isArabic ? "صح/خطأ يجب أن يحتوي على خيارين" : "True/False needs exactly 2 options";
    if (correct !== 1) return isArabic ? "اختر إجابة صحيحة واحدة" : "Pick exactly one correct answer";
  } else if (body.type === "single_choice") {
    if (body.options.length < 2) return isArabic ? "على الأقل خياران" : "At least 2 options";
    if (correct !== 1) return isArabic ? "اختر إجابة صحيحة واحدة" : "Pick exactly one correct answer";
  } else {
    if (body.options.length < 2) return isArabic ? "على الأقل خياران" : "At least 2 options";
    if (correct < 1) return isArabic ? "اختر إجابة صحيحة واحدة على الأقل" : "Pick at least 1 correct";
  }
  return null;
}

export default function QuizEditorPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const { quizId } = useParams<{ quizId: string }>();
  const { course } = useCourse();
  const navigate = useNavigate();

  const [quiz, setQuiz] = React.useState<CourseQuiz | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [savingMeta, setSavingMeta] = React.useState(false);
  const [meta, setMeta] = React.useState<QuizUpsertBody>({
    title: "",
    pass_degree: 0,
    max_attempts: 1,
  });

  const [editingQuestion, setEditingQuestion] = React.useState<QuizQuestion | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);
  const [questionDraft, setQuestionDraft] = React.useState<QuizQuestionUpsertBody>({
    question: "",
    type: "single_choice",
    score: 1,
    options: [
      { answer: "", is_correct: false },
      { answer: "", is_correct: false },
    ],
  });
  const [deleteTarget, setDeleteTarget] = React.useState<QuizQuestion | null>(null);
  const [savingQuestion, setSavingQuestion] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!quizId) return;
    setLoading(true);
    try {
      const q = await instructorQuizzesService.get(course.id, quizId, { force: true });
      if (!q) {
        toastError(isArabic ? "الاختبار غير موجود" : "Quiz not found");
        navigate("../");
        return;
      }
      setQuiz(q);
      setMeta({ title: q.title, pass_degree: q.pass_degree, max_attempts: q.max_attempts });
    } finally {
      setLoading(false);
    }
  }, [course.id, quizId, isArabic, navigate]);

  React.useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setIsCreating(true);
    setEditingQuestion(null);
    setQuestionDraft({
      question: "",
      type: "single_choice",
      score: 1,
      options: [
        { answer: "", is_correct: false },
        { answer: "", is_correct: false },
      ],
    });
  };

  const openEdit = (q: QuizQuestion) => {
    setEditingQuestion(q);
    setIsCreating(false);
    setQuestionDraft({
      question: q.question,
      type: q.type,
      score: q.score,
      options: q.options.map((o) => ({ ...o })),
    });
  };

  const closeQuestionModal = () => {
    setIsCreating(false);
    setEditingQuestion(null);
  };

  const handleSaveMeta = async () => {
    if (!quiz) return;
    if (!meta.title.trim()) {
      toastError(isArabic ? "العنوان مطلوب" : "Title is required");
      return;
    }
    setSavingMeta(true);
    try {
      const updated = await instructorQuizzesService.update(course.id, quiz.id, {
        title: meta.title.trim(),
        pass_degree: Number(meta.pass_degree),
        max_attempts: Number(meta.max_attempts),
      });
      if (updated) setQuiz({ ...quiz, ...updated });
    } finally {
      setSavingMeta(false);
    }
  };

  const handleSaveQuestion = async () => {
    if (!quiz) return;
    const err = validateQuestion(questionDraft, isArabic);
    if (err) {
      toastError(err);
      return;
    }
    setSavingQuestion(true);
    try {
      if (editingQuestion) {
        await instructorQuizQuestionsService.update(
          course.id,
          quiz.id,
          editingQuestion.id,
          questionDraft
        );
      } else {
        await instructorQuizQuestionsService.create(course.id, quiz.id, questionDraft);
      }
      instructorQuizzesService.invalidateQuiz(course.id, quiz.id);
      await load();
      closeQuestionModal();
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!quiz || !deleteTarget) return;
    await instructorQuizQuestionsService.remove(course.id, quiz.id, deleteTarget.id);
    instructorQuizzesService.invalidateQuiz(course.id, quiz.id);
    setDeleteTarget(null);
    await load();
  };

  if (loading || !quiz) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="../"
          className="inline-flex items-center gap-1 text-sm font-semibold text-brand-500 hover:underline"
        >
          {isArabic ? <MdArrowForward className="h-4 w-4" /> : <MdArrowBack className="h-4 w-4" />}
          {isArabic ? "العودة للاختبارات" : "Back to quizzes"}
        </Link>
      </div>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="grid gap-4 md:grid-cols-3">
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 md:col-span-3">
            {isArabic ? "عنوان الاختبار *" : "Quiz title *"}
            <input
              value={meta.title}
              onChange={(e) => setMeta((p) => ({ ...p, title: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </label>
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
            {isArabic ? "درجة النجاح *" : "Pass score *"}
            <input
              type="number"
              min={0}
              value={meta.pass_degree}
              onChange={(e) => setMeta((p) => ({ ...p, pass_degree: Number(e.target.value) }))}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
            <span className="mt-1 block text-[11px] font-medium text-gray-400">
              {isArabic
                ? "بالدرجات (من مجموع درجات الأسئلة)، وليست نسبة مئوية"
                : "In points (of total question points), not a percentage"}
            </span>
          </label>
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
            {isArabic ? "أقصى عدد محاولات *" : "Max attempts *"}
            <input
              type="number"
              min={1}
              value={meta.max_attempts}
              onChange={(e) => setMeta((p) => ({ ...p, max_attempts: Number(e.target.value) }))}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </label>
          <div className="flex items-end justify-end">
            <button
              type="button"
              onClick={handleSaveMeta}
              disabled={savingMeta}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
            >
              <MdSave className="h-5 w-5" />
              {isArabic ? "حفظ" : "Save"}
            </button>
          </div>
        </div>
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-800 dark:text-white">
            {isArabic ? "الأسئلة" : "Questions"}
          </h3>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-600"
          >
            <MdAdd className="h-5 w-5" />
            {isArabic ? "إضافة سؤال" : "Add question"}
          </button>
        </div>

        {quiz.questions && quiz.questions.length > 0 ? (
          <ol className="flex flex-col gap-2">
            {quiz.questions.map((q, i) => (
              <li
                key={q.id}
                className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-navy-800"
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-sm font-bold text-brand-600 dark:bg-brand-500/10 dark:text-brand-200">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-800 dark:text-white">{q.question}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 dark:bg-white/10">
                        {q.type.replace("_", " ")}
                      </span>{" "}
                      · {isArabic ? "الدرجة" : "Score"}: {q.score} ·{" "}
                      {isArabic ? "الخيارات" : "Options"}: {q.options.length}
                    </p>
                    <ul className="mt-2 flex flex-wrap gap-1">
                      {q.options.map((opt, j) => (
                        <li
                          key={j}
                          className={`rounded-full border px-2 py-0.5 text-[11px] ${
                            opt.is_correct
                              ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200"
                              : "border-gray-200 text-gray-600 dark:border-white/10 dark:text-gray-300"
                          }`}
                        >
                          {opt.answer}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => openEdit(q)}
                      className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10"
                      aria-label={isArabic ? "تعديل" : "Edit"}
                    >
                      <MdEdit className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(q)}
                      className="rounded-lg p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                      aria-label={isArabic ? "حذف" : "Delete"}
                    >
                      <MdDelete className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <EmptyState
            icon={<MdQuestionAnswer className="h-6 w-6" />}
            title={isArabic ? "لا توجد أسئلة" : "No questions yet"}
            description={
              isArabic ? "أضف سؤالاً لبدء بناء الاختبار." : "Add a question to start building the quiz."
            }
            action={
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-600"
              >
                <MdAdd className="h-5 w-5" />
                {isArabic ? "إضافة سؤال" : "Add question"}
              </button>
            }
          />
        )}
      </section>

      <Modal
        open={isCreating || !!editingQuestion}
        onClose={closeQuestionModal}
        title={editingQuestion ? (isArabic ? "تعديل السؤال" : "Edit question") : isArabic ? "سؤال جديد" : "New question"}
        size="lg"
      >
        <div className="flex flex-col gap-4">
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
            {isArabic ? "نص السؤال *" : "Question text *"}
            <textarea
              value={questionDraft.question}
              onChange={(e) => setQuestionDraft((p) => ({ ...p, question: e.target.value }))}
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </label>
          <div className="grid gap-3 md:grid-cols-2">
            <Select
              label={isArabic ? "النوع *" : "Type *"}
              value={questionDraft.type}
              onChange={(next) => {
                const nextType = String(next) as QuizQuestionType;
                setQuestionDraft((p) => ({ ...p, type: nextType }));
              }}
              options={[
                {
                  value: "single_choice",
                  label: isArabic ? "اختيار من متعدد (إجابة واحدة)" : "Single choice",
                },
                {
                  value: "multiple_choice",
                  label: isArabic ? "اختيار من متعدد (عدة إجابات)" : "Multiple choice",
                },
                { value: "true_false", label: isArabic ? "صح / خطأ" : "True / False" },
              ]}
              isArabic={isArabic}
              allowEmpty={false}
              clearable={false}
              required
            />
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
              {isArabic ? "الدرجة *" : "Score *"}
              <input
                type="number"
                min={1}
                value={questionDraft.score}
                onChange={(e) => setQuestionDraft((p) => ({ ...p, score: Number(e.target.value) }))}
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              />
            </label>
          </div>
          <QuizOptionsEditor
            type={questionDraft.type}
            options={questionDraft.options}
            onChange={(next: QuizQuestionOption[]) =>
              setQuestionDraft((p) => ({ ...p, options: next }))
            }
          />
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={closeQuestionModal}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/10"
            >
              {isArabic ? "إلغاء" : "Cancel"}
            </button>
            <button
              type="button"
              onClick={handleSaveQuestion}
              disabled={savingQuestion}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
            >
              {isArabic ? "حفظ" : "Save"}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteQuestion}
        title={isArabic ? "حذف السؤال" : "Delete question"}
        description={
          isArabic
            ? "سيتم حذف هذا السؤال نهائيًا. هل أنت متأكد؟"
            : "This question will be deleted. Are you sure?"
        }
      />
    </div>
  );
}
